import { useCallback, useEffect, useRef, useState } from 'react';
import { useBookingEvents } from './useBookingEvents';
import * as extensions from '../services/extensions';
import { openRazorpay } from '../services/razorpay';
import { ApiError } from '../services/types';

/**
 * "Can my Pro stay a bit longer?", as a screen can hold it.
 *
 * ---------------------------------------------------------------------------
 * THE ONE THING THIS HOOK EXISTS FOR
 * ---------------------------------------------------------------------------
 * Asking is not the end of the story. The server answers with a Pro already
 * held - pay now - or with "we are arranging one", which is settled minutes
 * later by somebody at Homingo and reaches the phone as a push. Both endings
 * land on a screen that may never have been closed, so the state has to be
 * polled, not awaited.
 *
 * Polling is deliberately narrow: only while something is open, and only
 * every few seconds. A finished request stops it.
 */
export type ExtensionState = {
  /** The request being watched, or null when there is nothing open. */
  current: extensions.Extension | null;
  /** Asking, or paying. Both block the button. */
  busy: boolean;
  /** Something to show the customer, already written for them. */
  error: string | null;
  /** Ask for `minutes` more. Resolves to what the server decided. */
  ask: (minutes: number) => Promise<extensions.Extension | null>;
  /** Open the gateway for a held request, then confirm it. */
  pay: () => Promise<boolean>;
  /** Take back an open request - the wrong amount of time was chosen. */
  cancel: () => Promise<void>;
  /** Forget a finished request so the card goes back to its resting state. */
  dismiss: () => void;
};

const POLL_MS = 5000;

const messageFor = (thrown: unknown): string => {
  if (thrown instanceof ApiError && thrown.message) return thrown.message;
  return 'Something went wrong. Please try again.';
};

export function useExtension(
  bookingId: string,
  /** False while the job is not running: nothing is asked and nothing polls. */
  enabled: boolean,
  /** Called once hours are actually on the booking, to refresh the screen. */
  onApplied?: () => void,
): ExtensionState {
  const [current, setCurrent] = useState<extensions.Extension | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* The last state seen, so the poll can tell "paid just now" from "was
     already paid when this screen opened" - only the first refreshes. */
  const seen = useRef<string | null>(null);

  const remember = useCallback(
    (next: extensions.Extension | null) => {
      const was = seen.current;
      seen.current = next?.status ?? null;
      setCurrent(next);
      if (next?.status === 'paid' && was && was !== 'paid') onApplied?.();
    },
    [onApplied],
  );

  /* What is open on this booking, if anything. Run on mount so a request
     made before the screen was closed is still on it when it reopens. */
  useEffect(() => {
    if (!enabled) {
      setCurrent(null);
      return;
    }
    let alive = true;
    extensions
      .listExtensions(bookingId)
      .then(rows => {
        if (!alive) return;
        const open = rows.find(extensions.isOpen) ?? null;
        seen.current = open?.status ?? null;
        setCurrent(open);
      })
      .catch(() => {
        /* Silent: nothing is open as far as the screen is concerned, and the
           card simply offers to ask. The next tap reports its own failure. */
      });
    return () => {
      alive = false;
    };
  }, [bookingId, enabled]);

  /**
   * Re-read the open request and keep whatever moved.
   *
   * Shared by the timer below and by the socket beside it, so the two cannot
   * disagree about what counts as a change - and only a changed STATUS counts,
   * because `remember` is what decides whether the booking underneath gets
   * refreshed, and it must not fire on a response that said nothing new.
   */
  const syncOpen = useCallback(() => {
    if (!current) return;
    extensions
      .listExtensions(bookingId)
      .then(rows => {
        const same = rows.find(row => row.id === current.id);
        if (same && same.status !== current.status) remember(same);
      })
      .catch(() => {});
  }, [bookingId, current, remember]);

  /** Something is open and the answer is somebody else’s to give. */
  const waiting = enabled && !!current && extensions.isOpen(current);

  /* Only while something is waiting on somebody else. */
  useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(syncOpen, POLL_MS);
    return () => clearInterval(timer);
  }, [syncOpen, waiting]);

  /**
   * And the moment it is settled, rather than up to five seconds afterwards.
   *
   * What is being waited on here is a person at Homingo deciding - a Pro is
   * held and the customer is asked to pay, or the request is turned down - and
   * the customer is looking straight at the card while it happens.
   *
   * The poll above keeps its own cadence rather than being slowed to pay for
   * this. It is the floor on how late the answer can arrive when the socket
   * cannot be made at all; this is the quick path, not the only one.
   */
  useBookingEvents([bookingId], waiting, syncOpen);

  const ask = useCallback(
    async (minutes: number) => {
      setBusy(true);
      setError(null);
      try {
        const asked = await extensions.requestExtension(bookingId, minutes);
        remember(asked);
        return asked;
      } catch (thrown) {
        setError(messageFor(thrown));
        return null;
      } finally {
        setBusy(false);
      }
    },
    [bookingId, remember],
  );

  const pay = useCallback(async () => {
    if (!current) return false;
    setBusy(true);
    setError(null);
    try {
      const handoff = await extensions.openExtensionCheckout(
        bookingId,
        current.id,
      );
      const result = await openRazorpay(handoff);
      await extensions.confirmExtensionCheckout(bookingId, current.id, result);
      remember({ ...current, status: 'paid' });
      onApplied?.();
      return true;
    } catch (thrown) {
      /* A customer who backed out of the gateway has not failed at anything -
         the hold is still theirs for the rest of its minutes. */
      const cancelled =
        thrown instanceof Error &&
        'kind' in thrown &&
        thrown.kind === 'cancelled';
      if (!cancelled) setError(messageFor(thrown));
      return false;
    } finally {
      setBusy(false);
    }
  }, [bookingId, current, onApplied, remember]);

  const cancel = useCallback(async () => {
    if (!current) return;
    setBusy(true);
    setError(null);
    try {
      await extensions.cancelExtension(bookingId, current.id);
      /* Cleared rather than kept as "cancelled": the customer asked for this
         to go away, and the card should be offering again on the next frame. */
      seen.current = null;
      setCurrent(null);
    } catch (thrown) {
      setError(messageFor(thrown));
    } finally {
      setBusy(false);
    }
  }, [bookingId, current]);

  const dismiss = useCallback(() => {
    setCurrent(null);
    setError(null);
    seen.current = null;
  }, []);

  return { current, busy, error, ask, pay, cancel, dismiss };
}

export default useExtension;
